import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Input,
  Button,
  Table,
  Tag,
  Select,
  Spin,
  Modal,
  Form,
  InputNumber,
  message,
  Card,
} from "antd";
import {
  SearchOutlined,
  SwapOutlined,
  BankOutlined,
  SendOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import NumberFormat from "react-number-format";
import {
  _get_admin_reports,
  _lookup_admin_user,
  _get_admin_balance_statement,
} from "../../../common/axios_services";
import TransferModal from "../../../components/transactions/TransferModal";

const { Option } = Select;

const AdminReports = () => {
  const [loading, setLoading] = useState(false);
  const [salesPeriod, setSalesPeriod] = useState("this_week");
  const [searchAccountId, setSearchAccountId] = useState("");
  const [searchingUser, setSearchingUser] = useState(false);
  const [searchedUserData, setSearchedUserData] = useState(null);

  const [peopleData, setPeopleData] = useState({
    buyersCount: 0,
    businessCount: 0,
    referredCount: 0,
    ambassadorsCount: 0,
    driversCount: 0,
    staffsCount: 0,
  });

  const [balancesData, setBalancesData] = useState({
    masterAccount: { localBalance: 0, anchorBalance: 0 },
    walletAccountT1Business: { localBalance: 0, anchorBalance: 0 },
    t2Wallet: { localBalance: 0, anchorBalance: 0 },
    masterAccountBalance: 0,
    walletAccountT1BusinessBalance: 0,
    t2WalletBalance: 0,
  });

  const [salesData, setSalesData] = useState({
    orderCount: 0,
    orderNgn: 0,
    vendorsNgn: 0,
    othersNgn: 0,
    grossBalNgn: 0,
  });

  const [statementModalVisible, setStatementModalVisible] = useState(false);
  const [activeStatementAccount, setActiveStatementAccount] = useState("master");
  const [statementPeriod, setStatementPeriod] = useState("this_month");
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [statementData, setStatementData] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [salesPeriod]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [peopleRes, balancesRes, salesRes] = await Promise.allSettled([
        _get_admin_reports("people"),
        _get_admin_reports("balances"),
        _get_admin_reports("sales", salesPeriod),
      ]);

      if (peopleRes.status === "fulfilled" && peopleRes.value?.data) {
        const payload = peopleRes.value.data?.data || peopleRes.value.data;
        setPeopleData(payload);
      }
      if (balancesRes.status === "fulfilled" && balancesRes.value?.data) {
        const payload = balancesRes.value.data?.data || balancesRes.value.data;
        setBalancesData(payload);
      }
      if (salesRes.status === "fulfilled" && salesRes.value?.data) {
        const payload = salesRes.value.data?.data || salesRes.value.data;
        setSalesData(payload);
      }
    } catch (err) {
      console.error("Failed to load admin reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async () => {
    if (!searchAccountId.trim()) {
      message.warning("Please enter a User Account ID");
      return;
    }
    setSearchingUser(true);
    try {
      const res = await _lookup_admin_user(searchAccountId.trim());
      const payload = res?.data?.data || res?.data;
      if (payload) {
        setSearchedUserData(payload);
      } else {
        message.error("User account ID not found");
      }
    } catch (err) {
      message.error("User account ID not found");
    } finally {
      setSearchingUser(false);
    }
  };

  const handleOpenStatement = async (accType) => {
    setActiveStatementAccount(accType);
    setStatementModalVisible(true);
    fetchStatement(accType, statementPeriod);
  };

  const fetchStatement = async (accType, period) => {
    setLoadingStatement(true);
    try {
      const res = await _get_admin_balance_statement(accType, period);
      const payload = res?.data?.data || res?.data;
      setStatementData(payload);
    } catch (err) {
      console.error("Failed to load statement:", err);
    } finally {
      setLoadingStatement(false);
    }
  };

  const txColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={type === "CREDIT" ? "green" : "volcano"}>
          {type || "TX"}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (val) => (
        <span className="font-semibold text-gray-900 text-[15px]">
          <NumberFormat
            thousandSeparator={true}
            prefix={"₦"}
            className="numeric"
            displayType={"text"}
            value={parseFloat(val || 0).toFixed(2)}
          />
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc) => <span className="text-[15px]">{desc || "Transaction"}</span>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (
        <span className="text-[15px]">{date ? new Date(date).toLocaleDateString() : "—"}</span>
      ),
    },
  ];

  const getMasterVal = () => {
    if (balancesData.masterAccount?.localBalance !== undefined) return balancesData.masterAccount.localBalance;
    return balancesData.masterAccountBalance || 0;
  };

  const getMasterAnchorVal = () => {
    if (balancesData.masterAccount?.anchorBalance !== undefined) return balancesData.masterAccount.anchorBalance;
    return getMasterVal();
  };

  const getT1Val = () => {
    if (balancesData.walletAccountT1Business?.localBalance !== undefined) return balancesData.walletAccountT1Business.localBalance;
    return balancesData.walletAccountT1BusinessBalance || balancesData.walletAccountT1Business || 0;
  };

  const getT1AnchorVal = () => {
    if (balancesData.walletAccountT1Business?.anchorBalance !== undefined) return balancesData.walletAccountT1Business.anchorBalance;
    return getT1Val();
  };

  const getT2Val = () => {
    if (balancesData.t2Wallet?.localBalance !== undefined) return balancesData.t2Wallet.localBalance;
    return balancesData.t2WalletBalance || 0;
  };

  const getT2AnchorVal = () => {
    if (balancesData.t2Wallet?.anchorBalance !== undefined) return balancesData.t2Wallet.anchorBalance;
    return getT2Val();
  };

  return (
    <Spin spinning={loading}>
      <div className="pb-40">

        {/* 1. PEOPLE REPORTS */}
        <div className="mb-6 bg-white p-5 border-gray-200">
          <h3 className="font-gilroyBold mb-5 text-[15px]">People Reports</h3>
          <Row gutter={[20, 20]}>
            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal text-[15px]">Buyers</p>
                <p className="stat-value-minimal text-[15px] font-gilroyBold">
                  {(peopleData.buyersCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal text-[15px]">Business</p>
                <p className="stat-value-minimal text-[15px] font-gilroyBold">
                  {(peopleData.businessCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal text-[15px]">Referred</p>
                <p className="stat-value-minimal text-[15px] font-gilroyBold">
                  {(peopleData.referredCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal text-[15px]">Ambassadors</p>
                <p className="stat-value-minimal text-[15px] font-gilroyBold">
                  {(peopleData.ambassadorsCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal text-[15px]">Drivers</p>
                <p className="stat-value-minimal text-[15px] font-gilroyBold">
                  {(peopleData.driversCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal text-[15px]">Staffs</p>
                <p className="stat-value-minimal text-[15px] font-gilroyBold">
                  {(peopleData.staffsCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>
          </Row>

          {/* USER REPORT LOOKUP */}
          <div className="pt-5 border-t border-gray-100 mt-5">
            <h4 className="font-gilroyBold mb-3 text-[15px]">User Report Lookup (Enter User Account ID)</h4>
            <div className="flex gap-3 max-w-md mb-4">
              <Input
                placeholder="e.g. ACC102934"
                value={searchAccountId}
                onChange={(e) => setSearchAccountId(e.target.value)}
                onPressEnter={handleUserSearch}
                className="border-gray-300 text-[15px]"
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleUserSearch}
                loading={searchingUser}
                className="bg-[#0e1726] hover:bg-[#1b2a4a] text-white px-5 border-none flex items-center gap-2 text-[15px]"
              >
                Search
              </Button>
            </div>

            {searchedUserData && (
              <div className="mt-4 p-5 bg-gray-50 border border-gray-200 rounded-2xl space-y-6">
                
                {/* 1. USER DETAILS */}
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex justify-between items-center flex-wrap gap-2 border-b border-gray-100 pb-3 mb-4">
                    <h5 className="font-gilroyBold text-[15px] text-gray-900 m-0">USER DETAILS</h5>
                    <Tag color="blue" className="text-[14px] font-semibold px-3 py-1 rounded-md">
                      {(searchedUserData.user?.tier || "tier_1").toUpperCase()}
                    </Tag>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[15px]">
                    <div>
                      <div className="text-gray-500 text-[15px]">Full Name:</div>
                      <div className="font-semibold text-gray-900 text-[15px]">
                        {searchedUserData.user?.firstName} {searchedUserData.user?.lastName}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Account ID:</div>
                      <div className="font-mono font-semibold text-gray-900 text-[15px]">
                        {searchedUserData.user?.accountId}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Role:</div>
                      <div className="font-semibold text-gray-900 text-[15px]">
                        {searchedUserData.user?.role || "USER"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Email:</div>
                      <div className="font-medium text-gray-900 text-[15px]">{searchedUserData.user?.email || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Phone:</div>
                      <div className="font-medium text-gray-900 text-[15px]">{searchedUserData.user?.phoneNumber || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Verification Status:</div>
                      <div className="font-semibold text-gray-900 text-[15px] capitalize">
                        {searchedUserData.user?.kycStatus || "pending"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. WALLET BALANCE */}
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <h5 className="font-gilroyBold text-[15px] text-gray-900 mb-4 border-b border-gray-100 pb-3">WALLET BALANCE</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[15px]">
                    <div>
                      <div className="text-gray-500 text-[15px]">Wallet Balance:</div>
                      <div className="font-gilroyBold text-[15px] text-gray-900">
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={"₦"}
                          className="numeric"
                          displayType={"text"}
                          value={parseFloat(searchedUserData.wallet?.balance || 0).toFixed(2)}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Sales Balance:</div>
                      <div className="font-gilroyBold text-[15px] text-gray-900">
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={"₦"}
                          className="numeric"
                          displayType={"text"}
                          value={parseFloat(searchedUserData.user?.salesBalance || 0).toFixed(2)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. TRANSACTIONS */}
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <h5 className="font-gilroyBold text-[15px] text-gray-900 mb-4 border-b border-gray-100 pb-3">TRANSACTIONS</h5>
                  {searchedUserData.recentTransactions?.length > 0 ? (
                    <Table
                      dataSource={searchedUserData.recentTransactions}
                      columns={txColumns}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      className="bg-white border border-gray-200 overflow-hidden"
                    />
                  ) : (
                    <div className="text-[15px] text-gray-500 py-2">No transaction history found for this user.</div>
                  )}
                </div>

                {/* 4. REFERRAL INFO */}
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <h5 className="font-gilroyBold text-[15px] text-gray-900 mb-4 border-b border-gray-100 pb-3">REFERRAL INFO</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[15px]">
                    <div>
                      <div className="text-gray-500 text-[15px]">Referral Code:</div>
                      <div className="font-mono font-semibold text-gray-900 text-[15px]">
                        {searchedUserData.user?.referralCode || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Referred By:</div>
                      <div className="font-semibold text-gray-900 text-[15px]">
                        {searchedUserData.user?.referredBy || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[15px]">Total Referrals:</div>
                      <div className="font-semibold text-gray-900 text-[15px]">
                        {searchedUserData.user?.referredCount || 0}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>

        {/* 2. BALANCES REPORTS */}
        <div className="mb-6 bg-white p-5 border-gray-200">
          <h3 className="font-gilroyBold mb-5 text-[15px]">Balances Reports</h3>
          <Row gutter={[20, 20]}>
            <Col xs={24} sm={8} md={8}>
              <div className="stat-card-minimal p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className="stat-label-minimal text-[15px] font-gilroyBold">Master Account</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-[15px]">
                    <span className="text-gray-500">Local Value:</span>
                    <span className="font-semibold text-gray-900">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"₦"}
                        className="numeric"
                        displayType={"text"}
                        value={parseFloat(getMasterVal()).toFixed(2)}
                      />
                    </span>
                  </div>
                  <div className="flex justify-between text-[15px]">
                    <span className="text-gray-500">Anchor Value:</span>
                    <span className="font-semibold text-green-700">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"₦"}
                        className="numeric"
                        displayType={"text"}
                        value={parseFloat(getMasterAnchorVal()).toFixed(2)}
                      />
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200 flex gap-2">
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => handleOpenStatement("master")}
                    className="w-full text-[14px]"
                  >
                    Statement
                  </Button>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={8} md={8}>
              <div className="stat-card-minimal p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className="stat-label-minimal text-[15px] font-gilroyBold">Wallet Account (T1 / Business)</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-[15px]">
                    <span className="text-gray-500">Local Value:</span>
                    <span className="font-semibold text-gray-900">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"₦"}
                        className="numeric"
                        displayType={"text"}
                        value={parseFloat(getT1Val()).toFixed(2)}
                      />
                    </span>
                  </div>
                  <div className="flex justify-between text-[15px]">
                    <span className="text-gray-500">Anchor Value:</span>
                    <span className="font-semibold text-green-700">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"₦"}
                        className="numeric"
                        displayType={"text"}
                        value={parseFloat(getT1AnchorVal()).toFixed(2)}
                      />
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200 flex gap-2">
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => handleOpenStatement("t1_business")}
                    className="w-full text-[14px]"
                  >
                    Statement
                  </Button>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={8} md={8}>
              <div className="stat-card-minimal p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className="stat-label-minimal text-[15px] font-gilroyBold">T2 Wallet</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-[15px]">
                    <span className="text-gray-500">Local Value:</span>
                    <span className="font-semibold text-gray-900">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"₦"}
                        className="numeric"
                        displayType={"text"}
                        value={parseFloat(getT2Val()).toFixed(2)}
                      />
                    </span>
                  </div>
                  <div className="flex justify-between text-[15px]">
                    <span className="text-gray-500">Anchor Value:</span>
                    <span className="font-semibold text-green-700">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"₦"}
                        className="numeric"
                        displayType={"text"}
                        value={parseFloat(getT2AnchorVal()).toFixed(2)}
                      />
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200 flex gap-2">
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => handleOpenStatement("t2")}
                    className="w-full text-[14px]"
                  >
                    Statement
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <div className="pt-5 border-t border-gray-100 mt-5 flex flex-col gap-3">
            <TransferModal onComplete={fetchReports}>
              <Button
                icon={<SwapOutlined />}
                className="w-full px-4 border-gray-300 text-gray-800 hover:border-gray-400 text-[15px]"
              >
                Transfer from Master (User ID & Bank)
              </Button>
            </TransferModal>
            <TransferModal isBusiness={true} onComplete={fetchReports}>
              <Button
                icon={<SwapOutlined />}
                className="w-full px-4 border-gray-300 text-gray-800 hover:border-gray-400 text-[15px]"
              >
                Transfer from Business (Bank & Staffs)
              </Button>
            </TransferModal>
          </div>
        </div>

        {/* STATEMENT MODAL */}
        <Modal
          title={
            <span className="font-gilroyBold text-[16px]">
              Account Statement ({activeStatementAccount.toUpperCase().replace("_", " ")})
            </span>
          }
          open={statementModalVisible}
          onCancel={() => setStatementModalVisible(false)}
          style={{ top: 20 }}
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
          footer={[
            <Button key="close" onClick={() => setStatementModalVisible(false)}>
              Close
            </Button>,
          ]}
          width={750}
        >
          <div className="space-y-4 pt-2 max-h-[65vh] overflow-y-auto pr-2">
            <div className="flex justify-between items-center gap-4 sticky top-0 bg-white py-2 z-10">
              <Select
                value={statementPeriod}
                onChange={(val) => {
                  setStatementPeriod(val);
                  fetchStatement(activeStatementAccount, val);
                }}
                style={{ width: 160 }}
              >
                <Option value="this_week">This Week</Option>
                <Option value="this_month">This Month</Option>
                <Option value="this_qtr">This Quarter</Option>
                <Option value="this_yr">This Year</Option>
              </Select>
            </div>

            <Spin spinning={loadingStatement}>
              {statementData?.transactions?.length > 0 ? (
                <Table
                  dataSource={statementData.transactions}
                  columns={txColumns}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ y: 350 }}
                />
              ) : (
                <div className="py-8 text-center text-gray-500 text-[15px]">
                  No statement records found for this period.
                </div>
              )}
            </Spin>
          </div>
        </Modal>

        {/* 3. SALES REPORTS */}
        <div className="mb-6 bg-white p-5 border-gray-200">
          <div className="flex justify-between items-center flex-wrap gap-4 mb-5">
            <h3 className="font-gilroyBold m-0 text-[14px]">Sales Reports</h3>
            <div className="flex items-center gap-2">
              {/* <span className="text-[14px] text-gray-500">Filter Period:</span> */}
              <Select
                value={salesPeriod}
                onChange={setSalesPeriod}
                style={{ width: 140 }}
              >
                <Option value="this_week">This Week</Option>
                <Option value="last_week">Last Week</Option>
                <Option value="this_month">This Month</Option>
                <Option value="this_qtr">This QTR</Option>
                <Option value="this_yr">This Yr</Option>
              </Select>
            </div>
          </div>

          <Row gutter={[20, 20]}>
            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal">Order#</p>
                <p className="stat-value-minimal">
                  {(salesData.orderCount || 0).toLocaleString()}
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal">Order NGN</p>
                <p className="stat-value-minimal">
                  <NumberFormat
                    thousandSeparator={true}
                    prefix={"₦"}
                    className="numeric"
                    displayType={"text"}
                    value={parseFloat(salesData.orderNgn || 0).toFixed(2)}
                  />
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal">Vendors NGN</p>
                <p className="stat-value-minimal">
                  <NumberFormat
                    thousandSeparator={true}
                    prefix={"₦"}
                    className="numeric"
                    displayType={"text"}
                    value={parseFloat(salesData.vendorsNgn || 0).toFixed(2)}
                  />
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal">Others NGN (SI + RI)</p>
                <p className="stat-value-minimal">
                  <NumberFormat
                    thousandSeparator={true}
                    prefix={"₦"}
                    className="numeric"
                    displayType={"text"}
                    value={parseFloat(salesData.othersNgn || 0).toFixed(2)}
                  />
                </p>
              </div>
            </Col>

            <Col xs={12} sm={8} md={6}>
              <div className="stat-card-minimal">
                <p className="stat-label-minimal">Gross Bal NGN</p>
                <p className="stat-value-minimal">
                  <NumberFormat
                    thousandSeparator={true}
                    prefix={"₦"}
                    className="numeric"
                    displayType={"text"}
                    value={parseFloat(salesData.grossBalNgn || 0).toFixed(2)}
                  />
                </p>
              </div>
            </Col>
          </Row>
        </div>

      </div>
    </Spin>
  );
};

export default AdminReports;
